// src/app/appRouting.js
// Deep link (mount) ve popstate (geri/ileri) handler'larını App.js'ten ayrıştıran modül.
// useAppRouting hook'u App.js'te çağrılır; setActivePage, setModalContent vb.
// artık navigationStore'dan gelir.
import { useEffect } from "react";
import { doc, getDoc, collection, query, where, limit, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { derinBaglantiyiCoz, docOkumasi, sorguOkumasi } from "./deepLinkResolve";
import type { DerinDurum } from "./deepLinkResolve";
import { isSheetRouteActive } from "../utils/sheetRouteGuard";
import { ROUTER_REZERVE } from "../constants/reservedUsernames";
// GÖNDERİ YORUM PANELİ yolu (`/p/{id}/comments`) — şema TEK KAYNAK
// `gonderiYorumUrl.ts`te; regex buraya KOPYALANMAZ (`yoldanBilgi` /
// `yoldanKoleksiyon` ile aynı desen).
import {
  GONDERI_YORUM_SORGUSU,
  gonderiYorumPaneliYolu,
  gonderiYorumPaneliYolundaMi,
  yoldanGonderiYorumPaneli,
} from "./gonderiYorumUrl";
import { setPendingV3Anchor } from "../myclipsv3/feed/pendingV3Anchor";
// P0-1 — "V3 klip nedir" TEK OTORİTE (satır-içi üçlü koşulun yerine).
import { klipV3Mu } from "./klipAcmaKarari";
// `clipsRepo` STATİK İMPORT'TAN ÇIKARILDI (#1019) — tek tüketici
// `openClipFromUrl` ve orada dinamik `import()` ile, ağ okumasıyla PARALEL
// yükleniyor (gerekçe + ölçüm o fonksiyonun başındaki blokta).
import {
  buildDiscoverySyncDetail,
  DISCOVERY_SYNC_EVENT,
  buildTabSyncDetail,
  TAB_SYNC_EVENT,
} from "../myclipsv3/feed/discoveryUrl";
// Composer/kamera + check-in URL'i — TEK KAYNAK (matcher regex'i burada
// KOPYALANMAZ; `olusturUrl.ts` üretir ve çözer).
import {
  mekaniStatedenOku,
  olusturModaliMi,
  olusturYolu,
  olusturYolundaMi,
  yoldanOlustur,
} from "./olusturUrl";
import { isPostUploading } from "../services/postUploadGuard";
// P0-6 — composer'ın kapanış onayı (soru metni orada, burada yalnız izin sorulur).
import { gonderiKapanabilirMi } from "../services/gonderiKapanisKapisi";
import { klipDuzenleSogukYukKarari } from "../myclipsv3/edit/editClipSheetUrl";
// Anlatım yüzeyi URL'i (yıldız/Gold/MyCards/yolculuk) — TEK KAYNAK.
import { yoldanBilgi } from "./bilgiSayfasiUrl";
// Koleksiyon detayı URL'i — TEK KAYNAK (matcher regex'i burada KOPYALANMAZ).
import { yoldanKoleksiyon } from "./koleksiyonUrl";
// Bildirim yolunun (`/notifications`) TEK yüklemi — regex burada KOPYALANMAZ.
import { bildirimYolundaMi } from "./gorunumFiltreUrl";
// `App.tsx`in çözücüden ÖNCE karşıladığı yollar — ölü rota kapısının
// muafiyeti. Kelime listesi DEĞİL, App'in fiilen kullandığı yüklemler.
import { cozucuOncesiYolMu } from "./cozucuOncesiRotalar";
// Tip-yalnız: derlemede silinir, çalışma-zamanı bağı YOK (store bu dosyayı
// import etmiyor → döngü de yok). Aşağıda yeniden dışa aktarılıyor.
import type { ModalVerisi } from "../store/navigationStore";

// ─── Yönlendirme sözleşmesi (TİP — rota şeması DEĞİŞMEDİ) ────────────────────
//
// ⚠️ Bu blok YALNIZ tip ekler. URL şeması, matcher regex'leri ve rezerve
// kelime listesi (ROUTER_REZERVE) BU TURDA DOKUNULMADI — rezerve testi
// `firestore.rules` ile birebir eşitlik istiyor.
//
// Şekiller GÖVDEDEN okundu: aşağıdaki her `set*` çağrısının argümanı + üç
// çağrı yeri (App.tsx:312/321, appRouting.test.ts makeActions).

/** `modalContent` değerleri dize ya da `null` (kapalı). */
export type ModalIcerigi = string | null;

/**
 * `setModalData`ya geçen yük.
 *
 * ⚠️ ARTIK BURADA TANIMLI DEĞİL — sahibi durumu tutan store. Burada ikinci bir
 * kopya yaşadığı sürece iki bildirim sessizce ayrışabiliyordu (ve ayrıştı:
 * buradaki `Record<string, unknown> | null` `string` dalını yutuyordu, store
 * ise `unknown` diyordu). Tek kaynak → ayrışacak ikinci kopya yok.
 * Tip-yalnız yeniden dışa aktarım: derlemede silinir, çalışma-zamanı bağı YOK.
 */
export type { ModalVerisi };

/** Modal kapanışında geri yüklenecek önceki durum (App.tsx `modalReturnRef`). */
export type ModalDonusDurumu = {
  modalContent?: ModalIcerigi;
  modalData?: ModalVerisi;
  pushedByApp?: boolean;
};

/**
 * `resolvePathToPage` ve iki hook'un ORTAK eylem seti.
 *
 * ⚠️ `setActivePage` ZORUNLU: `resolvePathToPage` onu on bir dalda KOŞULSUZ
 * çağırıyor (explore/clips/map/profile…). `openClipFromUrl`'deki
 * `typeof setActivePage === "function"` savunması kaldırılmadı ama tipi
 * belirlemez — orası tek dal, burası sözleşme.
 * `pushedByAppRef` isteğe bağlı: her kullanım `if (pushedByAppRef)` ile korunuyor.
 */
export type RotaEylemleri = {
  setActivePage: (page: string) => void;
  setModalContent: (content: ModalIcerigi) => void;
  setModalData: (data: ModalVerisi) => void;
  setProfileRouteUsername: (username: string) => void;
  pushedByAppRef?: { current: boolean } | null;
  /**
   * O anki `activePage`, REF olarak. (#888'de yalnız `usePopstateHandler`in
   * kendi ek tipindeydi; P0-1'de ORTAK sözleşmeye taşındı — `resolvePathToPage`
   * `/c/{id}` dalının da buna ihtiyacı var: V3 feed'i ZATEN mount'luysa devir
   * `pendingV3Anchor` ile DEĞİL OLAYLA yapılmalıdır, çünkü pending yalnız
   * `FeedContainerV3` MOUNT'ında tüketilir → mount'lu feed'de sessizce bayatlar
   * ve bir sonraki mount'ta YANLIŞ klibi açar.)
   *
   * Neden prop değil ref: popstate effect'inin bağımlılığı `[modalContent]`;
   * `activePage`i prop yapmak her sekme değişiminde dinleyiciyi kopar-tak
   * ederdi (davranış riski, kazanç yok).
   *
   * Opsiyonel: verilmezse ona bağlı davranışlar hiç çalışmaz (eski davranış).
   */
  activePageRef?: { current: string } | null;
};

/**
 * Derin-bağlantı açıcılarının GERÇEKTEN kullandığı alt küme — `Pick` ile
 * TÜRETİLDİ, elle yeniden yazılmadı. Dört çağrı yeri (`derinDurumGoster`,
 * `openPostFromUrl`, `openStoryFromUrl` ve `openClipFromUrl`'ün ortak kısmı)
 * `setActivePage`/`setProfileRouteUsername` GEÇMİYOR.
 */
type DerinBaglantiEylemleri = Pick<
  RotaEylemleri,
  "setModalContent" | "setModalData" | "pushedByAppRef"
> & {
  /**
   * Açıcı başlatıldığı andaki gezinme sürümü (#888). Asenkron çözüm döndüğünde
   * sürüm değişmişse yazım DÜŞÜRÜLÜR — bayat kart yeni ekranın üstüne basmaz.
   * Opsiyonel: verilmezse eski (korumasız) davranış birebir korunur.
   */
  gezinmeSurumu?: number;
};

// ─── ROUTER MODAL KÜTÜĞÜ — TEK KAYNAK (#837) ─────────────────────────────────
//
// 🩺 KUSUR (sanal dünya, gerçek jestlerle üretildi): klip → GERİ → içerik
// silindi → İLERİ (`/c/{id}` "İçerik bulunamadı", URL korunur ✅) → GERİ →
// **kart ekranda kaldı, adres çubuğu `/`**. Yani "URL silindi" değil,
// **"URL değişti, katman kapanmadı"**.
//
// 🧬 KÖK SEBEP — *ÇÜRÜYEN LİSTE*: `usePopstateHandler` kapanacak katmanları
// ELLE YAZILMIŞ iki isimle tanıyordu (`["viewingComments","viewingClip"]`).
// `derinBaglantiDurumu` (#94.4'te eklendi) ve `discovery` o listeye HİÇ
// yazılmadı — çünkü listeyi güncellemeyi hatırlatan hiçbir mekanizma yoktu.
// Listeyi UZATMAK düzeltme DEĞİLDİR: aynı çürümenin bir sonraki turudur.
//
// 🔧 KURAL DEĞİŞİKLİĞİ: kapanış kararı artık **türetilir**. Router'ın URL'den
// açabildiği HER modal bu kütükte kayıtlı olmak ZORUNDA — çünkü açıcı dallar
// modal adını artık `katman(...)` üzerinden alır ve `katman`ın parametre tipi
// `keyof typeof ROUTER_MODAL_KATMANLARI`dır. Kütüğe yazılmamış bir ad
// **derlenmez** (`npm run kapi` → `tsc`). Yani liste sessizce çürüyemez:
// yeni bir URL-modalı eklemek, onu sınıflandırmayı ZORUNLU kılar.
//
// Değer = popstate'te katmanı KİM sahiplenir:
//   "url"   → varlığını URL taşır. `resolvePathToPage` yeni yolu çözemiyorsa
//             (`!resolved`) katmanın altında URL kalmamıştır → KAPANIR.
//   "kendi" → yukarıda kendi erken-dönüş dalı var (modalReturnRef restore,
//             DM'in kendi senkronu, composer'ın bir-üst-adım daveti…).
//             Bu jenerik kural ONA DOKUNMAZ.
export const ROUTER_MODAL_KATMANLARI = {
  // URL-katmanı: `!resolved` olduğunda kapanırlar.
  derinBaglantiDurumu: "url",
  viewingComments: "url",
  viewingClip: "url",
  discovery: "url",
  // Kendi dalı olanlar (aşağıdaki erken dönüşler yönetir):
  viewingStory: "kendi",
  placeDetail: "kendi",
  messages: "kendi",
  notifications: "kendi",
  newCheckInDetail: "kendi",
} as const;

/** Kütüğe kayıtlı router modal adları — `katman()` bunu tipe çevirir. */
export type RouterModalAdi = keyof typeof ROUTER_MODAL_KATMANLARI;

/**
 * Router'ın modal adını ÜRETTİĞİ tek kapı. Ham dize literali yerine bunu
 * kullanmak, adın kütükte kayıtlı olmasını DERLEME ZAMANINDA zorunlu kılar.
 * (Çalışma zamanında kimlik fonksiyonu — davranış deltası sıfır.)
 */
const katman = (ad: RouterModalAdi): string => ad;

/** Kütükten TÜRETİLİR — elle ikinci bir liste yazılmaz. */
export const URL_KATMANI_MODALLARI: ReadonlySet<string> = new Set(
  Object.keys(ROUTER_MODAL_KATMANLARI).filter(
    (ad) => ROUTER_MODAL_KATMANLARI[ad as RouterModalAdi] === "url",
  ),
);

/** popstate'in kapanış testi. `modalContent` bir URL-katmanı mı? */
export const urlKatmaniModaliMi = (icerik?: ModalIcerigi): boolean =>
  typeof icerik === "string" && URL_KATMANI_MODALLARI.has(icerik);

// ─── KÖK YOLU OLMAYAN SAYFALAR (#888) ────────────────────────────────────────
//
// Kendi (kök-DIŞI) URL'si olan sayfalar. Adres çubuğu `/` iken bunlardan biri
// mount'lu kalmışsa ekran ile URL YALAN SÖYLÜYOR demektir — bu liste tam olarak
// o durumu tanımak için var.
//
// 📏 UYDURULMADI, `resolvePathToPage` GÖVDESİNDEN okundu: aşağıdaki her ad,
// kök olmayan bir matcher dalında `setActivePage(...)` argümanı olarak geçiyor
// (`/explore` · `/map` · `/discoveries` · `/mylive` · `/settings/blocked` ·
// `/settings/hidden-words` · `/settings/suggestions` ·
// `/admin/reports` · `/admin/cost-dashboard` · `/creator/clips` · `/u/{ad}`).
//
// 🚫 BİLEREK DIŞARIDA: `home` ve `clips`. İkisinin de taban yolu `/`dır
// (`App.handleNavChange`: `tab === "home" || tab === "clips"` → `replaceState("/")`),
// dolayısıyla `/` üzerinde durmaları BAYAT DEĞİL, DOĞRU. MyClips beslemesindeki
// kullanıcıyı home'a atmak gerçek bir regresyondur.
export const KOK_YOLU_OLMAYAN_SAYFALAR: ReadonlySet<string> = new Set([
  "explore",
  "map",
  "discoveries",
  "mylive",
  "blockedUsers",
  "gizliKelimeler",
  "oneriAyarlari",
  "adminReports",
  "adminCostDashboard",
  "creatorDashboard",
  "profile",
]);

/**
 * KÖKÜ TABAN KABUL EDEN SEKMELER — `/` onlar için BAYAT DEĞİL, DOĞRUdur.
 *
 * `App.handleNavChange`: `tab === "home" || tab === "clips"` → `replaceState("/")`.
 * Yani bu ikisi adres çubuğunda `/` görürken YERİNDEDİR; birini diğerine
 * çevirmek kullanıcıyı sekmesinden atmaktır.
 *
 * Neden `KOK_YOLU_OLMAYAN_SAYFALAR`in tümleyeni olarak ELLE yazıldı: o küme
 * "kendi URL'si olan sayfalar"ın LİSTESİdir ve kapalı değildir (kütükte
 * olmayan bir sayfa adı da `/` üzerinde durabilir). Guard'ı pozitif kümeyle
 * kurmak, listede olmayan her sekmeyi sessizce muaf tutardı — kapsam kaybı.
 * Negatif küme yalnız ÖLÇÜLMÜŞ iki adı muaf tutar, gerisi eski davranışta
 * kalır.
 */
export const KOK_TABANLI_SAYFALAR: ReadonlySet<string> = new Set(["home", "clips"]);

// ─── GEZİNME SÜRÜMÜ — BAYAT ASENKRON ÇÖZÜMÜN PANZEHİRİ (#888) ────────────────
//
// 🩺 KUSUR (sanal dünya, 9 turluk koşu, tohum 3 ve 8): Firestore arızasında
// `/p/{id}` · `/c/{id}` · `/stories/{u}/{id}` açıcıları ULASILAMADI'ya düşüp
// «Bağlantı kurulamadı» kartını basıyor. Ama bu açıcılar **ateşle-unut**:
// arıza sırasında okuma yavaşlıyor, kullanıcı (ya da dünyanın SPA gezinmesi)
// çoktan BAŞKA bir yola geçmiş oluyor, ve geç dönen çözüm kartı **yeni yolun
// üstüne** basıyor. Ölçülen sonuç: `/discoveries` ve `/p/{id}` **birebir aynı**
// ekranı gösteriyordu (metin=175, ana=0) → *tek ekran, çok etiket*; adres
// değişiyor, içerik değişmiyor. Tur 3'te arıza kalkmadığı için kart tur sonuna
// kadar kilitli kaldı ve tek çıkış `window.location.reload()` idi.
//
// 🔧 ÇÖZÜM — sayaç, yol KARŞILAŞTIRMASI DEĞİL: açıcı başlatıldığı andaki
// gezinme sürümünü taşır; yazmadan önce sürümün HÂLÂ güncel olduğunu doğrular.
// Neden `window.location.pathname` karşılaştırması DEĞİL: `?c=`/`?p=` sorgu
// permalinkleri açıcıyı başlattıktan HEMEN SONRA `replaceState("/")` yapıyor
// (Android geri-tuşu döngüsü fix'i) — yol karşılaştırması o meşru dalı da
// bayat sayıp klibi hiç açmazdı. Sayaç YALNIZ `resolvePathToPage` girişinde
// artar, dolayısıyla aynı çözümlemenin kendi `replaceState`'i onu etkilemez.
let _gezinmeSurumu = 0;

/** Test/tanı için okunur; ürün kodu yalnız `bayatCozum` üzerinden kullanır. */
export const gezinmeSurumu = (): number => _gezinmeSurumu;

/**
 * Bu asenkron çözüm, başladığından beri gerçekleşen bir gezinme yüzünden
 * ARTIK GEÇERSİZ mi? `surum` verilmemişse (eski çağrı yeri) daima geçerli
 * sayılır — davranış deltası sıfır.
 */
const bayatCozum = (surum?: number): boolean =>
  typeof surum === "number" && surum !== _gezinmeSurumu;

// ─── `?go=` — ÇIPLAK PROFİL REWRITE'ININ DÖNGÜ PANZEHİRİ ─────────────────────
//
// 🩺 Kapatılan kusur: `mylasa.app/{kullaniciAdi}` adres çubuğundan kopyalanıp
// paylaşılınca ÖNİZLEME ÇIKMIYORDU (çıplak yol `**` catch-all → ham
// `index.html`, jenerik meta). Çözüm: firebase.json'da `/*` →
// `renderProfileShare` (klip `/c/` ve gönderi `/p/` ile aynı desen).
//
// 🔁 Ama o rewrite bir DÖNGÜ doğurur: fonksiyon insanı `/{kullaniciAdi}`ye
// yollarsa aynı fonksiyon yeniden çağrılır. Bu yüzden fonksiyon insanı
// `/?go=/{kullaniciAdi}`ye `location.replace` eder — kök yol `**` → SPA boot
// eder ve BURASI adres çubuğunu gerçek yola yazıp o yolu çözer.
//
// Akrabaları: `?c=` (klip), `?p=` (gönderi), `?discover=1`. Fark: `?go=` bir
// içerik kimliği değil bir YOL taşır → sınırları dar tutulur (yalnız kendi
// origin'imizde tek eğik çizgiyle başlayan, sorgu/parça içermeyen yollar).
// Aksi halde bu, sitenin içinden dışarı yönlendiren bir kapı olurdu.
const GO_MAKS_UZUNLUK = 512;

export function goHedefiCoz(search: string | null | undefined): string | null {
  try {
    const ham = new URLSearchParams(search || "").get("go");
    if (!ham) return null;
    const yol = String(ham);
    if (yol.length > GO_MAKS_UZUNLUK) return null;
    // Tek `/` ile başlamalı: `//evil.com` protokol-relatif MUTLAK URL'dir,
    // `/\evil.com` bazı tarayıcılarda ona denktir.
    if (!yol.startsWith("/")) return null;
    if (yol.startsWith("//") || yol.startsWith("/\\")) return null;
    // Sorgu/parça/ters-eğik taşımasın — `?go=` bir YOL parametresidir.
    if (/[?#\\]/.test(yol)) return null;
    if (/^\/+$/.test(yol)) return null; // "/" → çözülecek bir şey yok
    return yol;
  } catch {
    return null;
  }
}

// `?go=` özyinelemesinin emniyet mandalı: `replaceState` bir sebeple
// çalışmazsa (izin/ortam) çözücü kendini sonsuz çağırabilirdi.
let _goDerinlik = 0;

// ─── Yardımcı: URL pattern eşleştirme ────────────────────────────────────────
// 🔴 ÖLÇÜLDÜ (2026-08-24): bu yüklem, modalı AÇAN matcher'dan DARDI. `mPost`
// (aşağıda, ~satır 580) dört şekli kabul ediyor:
//     /p/{id}  ·  /p/{id}/  ·  /p/{id}/c/{cid}  ·  /p/{id}/c/{cid}/r/{rid}
// bu yüklem ise YALNIZ birincisini. `appModals.tsx` kapanışta URL'i yalnız
// bu yüklem doğruysa temizlediği için, diğer ÜÇ şekille açılan modal
// kapatıldığında ADRES ÇUBUĞU YERİNDE KALIYORDU → sayfa yenilenince modal
// kendiliğinden geri geliyordu (kullanıcı onu kapatmıştı).
//
// 🧭 Kanonun "susturma yollarını say" kuralının bir örneği: aynı şemanın
// İKİ ayrı tanımı vardı ve biri sessizce dar kaldı. Artık tek gerçek:
// açan matcher ne kabul ediyorsa, kapanış onu temizler.
//
// ➕ 2026-09-02 — `/p/{id}/comments` (yorum PANELİ yolu) da bu yüklemin
// kapsamındadır. Yukarıdaki dersin birebir tekrarını önler: panel adresini
// açan matcher eklenip kapanış yüklemi dar bırakılsaydı, panel kapatıldığında
// adres çubuğu artık açık olmayan bir paneli iddia etmeye devam ederdi ve
// yenilemede panel kendiliğinden geri gelirdi. Şema KOPYALANMAZ —
// `gonderiYorumUrl.ts` tek kaynaktır.
export const isPostOrClipPermalinkPath = (pathname?: string | null) =>
  /^\/(p|c)\/[A-Za-z0-9_-]+(?:\/c\/[A-Za-z0-9_-]+)?(?:\/r\/[A-Za-z0-9_-]+)?\/?$/
    .test(pathname || "") || gonderiYorumPaneliYolundaMi(pathname);

export const isAnyPermalinkPath = (pathname?: string | null) =>
  isPostOrClipPermalinkPath(pathname);

// PR-V3c — legacy klip MODAL açılışında (App.tsx handleViewClip) /c/{clipId}
// pushState kararı. SAF fonksiyon (DOM/side-effect yok) → appRouting.test.ts ile
// robotla kanıtlanır; App.tsx yalnız kararı uygular (modalReturnRef + pushState).
// push=false → geçersiz/eksik clipId VEYA URL zaten hedefte (duplicate history
// guard). Post/mekan modalıyla aynı /c,/p,/place şeması; encode ile hizalı.
// 📏 ÇAĞRI YERİ SAYIMI (EMİR): computeClipModalPush( → 1 üretim + 6 test
//    App.tsx:696 `clip?.id` (tip bilinmiyor) · appRouting.test.ts 324/330/333/
//    338/345-348/351/356 → `null`, `undefined` ve `""` DE geçiliyor.
//    Bu yüzden `clipId: unknown`: gövde zaten `clipId == null ? "" : String()`
//    ile her şeyi karşılıyor; daraltmak testlerin kanıtladığı sözleşmeyi bozardı.
export function computeClipModalPush(clipId: unknown, currentPath?: string | null) {
  const id = clipId == null ? "" : String(clipId);
  if (!id) return { push: false, target: null, state: null };
  const target = `/c/${encodeURIComponent(id)}`;
  if (currentPath === target) return { push: false, target, state: null };
  return { push: true, target, state: { __clipModal: true, clipId: id } };
}

// ─── Firestore yardımcıları ───────────────────────────────────────────────────
//
// EMİR #94.4 — BEŞ DAL TEK PRİMİTİFE BAĞLANDI.
// Eskiden her fonksiyon kendi `!exists()` + `catch` mantığını yazıyordu ve
// HEPSİ `replaceState("/")` yapıyordu: "içerik yok" ile "içeriğe ulaşamadım"
// aynı kutuya düşüyor, üstüne adres çubuğu siliniyordu (yenileyip tekrar
// deneme imkânı da yok oluyordu). Artık tek karar noktası:
// `derinBaglantiyiCoz()` → BULUNDU / YOK / ULASILAMADI, ve URL KORUNUR.
function derinDurumGoster(
  durum: DerinDurum,
  { setModalData, setModalContent, pushedByAppRef, gezinmeSurumu: surum }: DerinBaglantiEylemleri,
) {
  // #888 — BAYAT KART BASMA. Bu çözüm başlatıldığından beri gezinildiyse kart
  // artık BAŞKA bir yolun ekranına ait olurdu ("adres değişti, içerik
  // değişmedi"). Sessizce düş: yeni yolun kendi çözümü zaten yürüyor.
  if (bayatCozum(surum)) return;
  setModalData({ derinDurum: durum === "ULASILAMADI" ? "ulasilamadi" : "yok" });
  setModalContent(katman("derinBaglantiDurumu"));
  if (pushedByAppRef) pushedByAppRef.current = false;
}
async function openPostFromUrl(
  id: string,
  {
    setModalData,
    setModalContent,
    pushedByAppRef,
    gezinmeSurumu: surum,
    commentId = null,
    replyId = null,
    yorumOdak = false,
  }: DerinBaglantiEylemleri & {
    commentId?: string | null;
    replyId?: string | null;
    /**
     * `/p/{id}/comments` (yorum PANELİ yolu) — çapa YOK, ama niyet var:
     * "bu gönderinin yorumları". Kanonik yüzey yorumları zaten satır-içi
     * render eder; bu bayrak onları GÖRÜŞ ALANINA getirir
     * (`PostDetailModalDesktop` tüketir). Çapalı permalinkle ÇAKIŞMAZ:
     * `/comments` şekli commentId taşımaz, dolayısıyla ikisi aynı anda dolamaz.
     */
    yorumOdak?: boolean;
  },
) {
  const cozum = await derinBaglantiyiCoz(async () => docOkumasi(await getDoc(doc(db, "posts", id))));
  if (cozum.durum !== "BULUNDU") {
    derinDurumGoster(cozum.durum, { setModalData, setModalContent, pushedByAppRef, gezinmeSurumu: surum });
    return;
  }
  // #888 — başarı dalı da bayat olabilir: kullanıcı beklerken başka yola
  // geçtiyse gönderi modalını onun üstüne AÇMAK aynı kusurun ikizidir.
  if (bayatCozum(surum)) return;
  {
    const snap = cozum.veri;
      // IG paritesi — /p/{id}/c/{commentId} yorum permalinki: anchor id'leri
      // modalData'ya geçir → `PostDetailModal` → `PostDetailModalDesktop`
      // (satır-içi yorum listesi, `CommentRow anchored=` ile vurgulama).
      //
      // 🔴 BU SATIR 2026-09-01'de DÜZELTİLDİ — eskiden "→ CommentSheetV3"
      // yazıyordu ve BU YANLIŞTI: `PostDetailModalDesktop` `CommentSheetV3`ü
      // ne import eder ne render eder (ölçüldü: dosyada 0 atıf); `CommentSheetV3`
      // KLİP yüzeyinindir (`EngagementRailV3`). Bayat yorum, permalinkin
      // "yanlış bileşene gittiği" teşhisini doğuruyordu — oysa bağlantı
      // DOĞRUYDU, ANLATIM yanlıştı. Kanon: *"bayat 'çalışmıyor' çıkarımı,
      // var olmayan bir arıza raporu doğurur."*
      // Kanoniklik `gonderiYorumKanonik.test.ts` ile AST üzerinden kilitli.
      setModalData({
        id: snap.id,
        type: "post",
        ...snap.data(),
        ...(commentId ? { initialCommentId: commentId } : {}),
        ...(replyId ? { initialReplyId: replyId } : {}),
        ...(yorumOdak ? { yorumOdak: true } : {}),
      });
      setModalContent(katman("viewingComments"));
      if (pushedByAppRef) pushedByAppRef.current = false;
  }
}

async function openClipFromUrl(
  id: string,
  {
    setModalData,
    setModalContent,
    setActivePage,
    pushedByAppRef,
    gezinmeSurumu: surum,
    toFeed = false,
    feedMountlu = false,
    commentId = null,
    replyId = null,
  }: DerinBaglantiEylemleri &
    Pick<RotaEylemleri, "setActivePage"> & {
      toFeed?: boolean;
      /**
       * V3 feed'i şu an EKRANDA mı (`activePage === "clips"`). Tahmin değil,
       * ölçüm: `resolvePathToPage`e verilen `activePageRef`ten okunur.
       * true → pending yerine `mlv3:anchor-clip` olayı (bkz. V3 dalı).
       */
      feedMountlu?: boolean;
      commentId?: string | null;
      replyId?: string | null;
    },
) {
  // ── #1019 YAVAŞ AĞ — klip çözücüsü ARTIK DİNAMİK YÜKLENİYOR ────────────
  // ÖLÇÜLDÜ (`main.*.js.map`, kaynak başına üretilen bayt): bu iki adın statik
  // import'u `clipsRepo.ts`yi (34 417 B) ve onun veri zincirini
  // (`turkeyCityCentroids` → `turkeyDistrictToCity` 10 225 B + `geofire-common`
  // 3 904 B) ANA PAKETE taşıyordu — yani MyClips'e HİÇ girmeyen bir kullanıcı
  // bile ~50 kB ham bedel ödüyordu. `yavas3g`de (51 200 B/sn) ≈ 1,0 sn, her
  // açılışta. Modül zaten MyClips akışında ayrı chunk olarak var; burada
  // paylaşılıyor, ikinci kez indirilmiyor.
  //
  // ⚠️ YENİ BİR "BAYAT" PENCERESİ AÇILMADI — bekleme KASTEN ağ okumasıyla
  // `Promise.all` içinde PARALEL. Yani mevcut `bayatCozum(surum)` guard'larının
  // SONRASINA yeni bir gap eklenmiyor; fonksiyonun sıralaması birebir korunuyor.
  // (Bu dosyanın kendi #888 dersi: "geç dönen başarı da bayat olabilir" — bu
  // yüzden gap sonraya değil, ÖNE ve paralele konuldu.)
  const [klipCozucu, cozum] = await Promise.all([
    import("../myclipsv3/feed/clipsRepo"),
    derinBaglantiyiCoz(async () => docOkumasi(await getDoc(doc(db, "clips", id)))),
  ]);
  const { mapPlainClipsToPlayable, clipFromSnap } = klipCozucu;
  if (cozum.durum !== "BULUNDU") {
    derinDurumGoster(cozum.durum, { setModalData, setModalContent, pushedByAppRef, gezinmeSurumu: surum });
    return;
  }
  // #888 — bkz. openPostFromUrl: geç dönen başarı da bayat olabilir.
  if (bayatCozum(surum)) return;
  {
    const snap = cozum.veri;
    const data = { id: snap.id, ...(snap.data() as any) };
    // IG /reels/{id} paritesi — paylaşılan/cold-load klip REELS FEED'inde o klipte
    // açılır (modal DEĞİL). ProfileMobile profil→reels deseni: pending anchor +
    // scoped → feed mount'ta SENKRON tüketir (kilitli playback/pager/poster'a
    // DOKUNMAZ). URL'i /c/ YAPMA (useClipDeepLink scoped'u ezer) — feed klibi
    // aktif edince onActiveClipChange zaten /c/{id} yazar. Yalnız V3 klipler feed'de
    // oynar; v2/legacy klip eski MODAL davranışında kalır.
    // 🔗 "V3 klip nedir" TEK OTORİTE: satır-içi üçlü koşul `klipAcmaKarari.ts`e
    // taşındı (P0-1). `App.handleViewClip` aynı yüklemi kullanır → akıştan
    // açılan klip ile paylaşılan bağlantı ARTIK AYNI kuralla yönlenir.
    const isV3 = klipV3Mu(data);
    // ── P0-1 · `/c/{id}` ARTIK DA FEED'E GİDER ────────────────────────────
    // ESKİDEN: `mClip` dalı `toFeed:false` geçiyordu, gerekçesi *"feed zaten
    // mount'lu olabilir → pending tüketilmez"*. Gerekçe DOĞRUYDU ama çözümü
    // yanlış yerdeydi: PAYLAŞILAN HER `/c/{id}` bağlantısı (denetim sahnesi
    // 58) zayıf legacy modalda açılıyordu. Artık mount durumu TAHMİN değil
    // ÖLÇÜM (`feedMountlu`), ve iki hâl ayrı ele alınır:
    //   · feed mount'lu DEĞİL → pending + sayfa geçişi (mount'ta tüketilir)
    //   · feed mount'lu       → `mlv3:anchor-clip` olayı (pending bayatlamaz)
    // Olay yolu `scopedClips` TAŞIR — `FeedContainerV3.onAnchor` scoped moda
    // yalnız payload'da dizi varsa girer; taşımasaydı global feed'e düşerdi
    // ("hep ilk klip"). Mapper zaten yukarıda dinamik yüklendi.
    if (toFeed && isV3 && typeof setActivePage === "function") {
      const scoped = mapPlainClipsToPlayable([data]);
      const scopedUid = data?.authorId || data?.uid || null;
      const scopedClips = scoped.length > 0 ? scoped : null;
      if (feedMountlu) {
        try {
          window.dispatchEvent(
            new CustomEvent("mlv3:anchor-clip", {
              detail: { clipId: id, scopedUid, scopedClips },
            }),
          );
        } catch (_) { /* no-op */ }
      } else {
        setPendingV3Anchor({ clipId: id, scopedUid, scopedClips });
        // URL'i "/" yap (/c/ DEĞİL): useClipDeepLink /c/'yi okuyup scoped'SUZ anchor
        // dispatch ederse "hep ilk klip" regresyonu olur. Feed klibi aktif edince
        // onActiveClipChange zaten /c/{id} yazacak.
        try { window.history.replaceState({}, "", "/"); } catch (_) {}
        setActivePage("clips");
      }
      klipYorumCapasiniYay(id, commentId, replyId);
      if (pushedByAppRef) pushedByAppRef.current = false;
      return;
    }
    // HAM DEĞİL NORMALİZE: `clipFromSnap` akışın kullandığı tek çözücü
    // (`v3_r2Url || mediaUrl || videoUrl` + `transformR2Url` + HLS manifesti).
    // Ham yayılım ÖNCE, normalize SONRA → ham alan okuyan hiçbir tüketici
    // kırılmaz, normalize alanlar (`src`/`hlsManifestUrl`/`posterUrl`) eklenir.
    setModalData({
      ...(snap.data() as any),
      ...clipFromSnap(snap),
      type: "clip",
      // Legacy/modal dalı için çapa; V3 feed dalı olayla haberleşir (aşağıda).
      ...(commentId ? { initialCommentId: commentId } : {}),
      ...(replyId ? { initialReplyId: replyId } : {}),
    });
    setModalContent(katman("viewingClip"));
    klipYorumCapasiniYay(id, commentId, replyId);
    if (pushedByAppRef) pushedByAppRef.current = false;
  }
}

/**
 * KLİP YORUM PERMALİNKİ — çapayı yorum sheet'ine ULAŞTIR.
 *
 * ⚠️ NEDEN OLAY, NEDEN PROP: yorum sheet'ini açan rail (`EngagementRailV3`)
 * MyClips V3 feed'inin İÇİNDE, kart başına mount olur; router'ın ona doğrudan
 * bir tutamağı yok. Bu yüzden ZATEN VAR OLAN köprü kullanılır:
 * `mlv3:open-comments` — bildirimden yorum açan yol (MyClipsV3Gate:537) tam
 * olarak bunu yayıyor ve rail yalnız KENDİ klibininkini dinliyor
 * (`id !== clip.id → return`). Yani kilitli oynatma/pager dosyalarına
 * DOKUNULMADAN çapa taşınır; ikinci bir "yorum açma" mekanizması icat edilmez.
 *
 * Gecikme de uydurma değil: bildirim yolu aynı sebeple 450ms bekliyor —
 * klip önce anchor'lanır, rail mount olur, sonra sheet açılır. Olay erken
 * yayılırsa dinleyici henüz yokken düşer (sessiz kayıp).
 */
const KLIP_YORUM_CAPA_GECIKMESI = 450;

/**
 * KOLEKSİYON DETAYI soğuk yükü. `ownerParam` UID de olabilir kullanıcı adı da.
 *
 * ⚠️ "Kullanıcı adı mı UID mi" ayrımı TAHMİN EDİLMEZ, ÖLÇÜLÜR: önce
 * `usernames/{ad}` dokümanı okunur; varsa oradaki uid kullanılır, yoksa
 * parametre ham UID kabul edilir. Ters sıra (önce UID varsay) sessizce yanlış
 * koleksiyonu değil, HİÇBİR koleksiyonu açardı ve sebebi görünmezdi.
 */
async function koleksiyonDetayiniAc(
  ownerParam: string,
  collectionId: string,
  surum?: number,
): Promise<void> {
  let ownerUid = ownerParam;
  try {
    const canon = String(ownerParam || "").trim().toLowerCase();
    const snap = await getDoc(doc(db, "usernames", canon));
    const uid = snap.exists()
      ? (snap.data() as { uid?: unknown } | undefined)?.uid
      : null;
    if (uid) ownerUid = String(uid);
  } catch {
    /* okuma başarısız → parametreyi ham UID kabul et (aşağıdaki fetch karar verir) */
  }
  // #888 — geç dönen çözüm bayat olabilir: kullanıcı beklerken başka yola
  // geçtiyse sheet'i onun üstüne AÇMA.
  if (bayatCozum(surum)) return;
  try {
    const m = await import("../components/Collections/collectionSheetHost");
    m.koleksiyonSheetiAc({ ownerUid, ownerParam, collectionId });
  } catch {
    /* detay yüzeyi kritik değil — yol yine de tanındı */
  }
}

function klipYorumCapasiniYay(
  clipId: string,
  commentId?: string | null,
  replyId?: string | null,
): void {
  if (!commentId || !clipId) return;
  if (typeof window === "undefined") return;
  setTimeout(() => {
    try {
      window.dispatchEvent(
        new CustomEvent("mlv3:open-comments", {
          detail: { clipId, commentId, replyId: replyId || null },
        }),
      );
    } catch (_) { /* çapa kozmetik — klip yine de açık */ }
  }, KLIP_YORUM_CAPA_GECIKMESI);
}

// IG paritesi — cold-load /stories/{username}/{storyId}: hikâye-viewer'ı (modal)
// o hikâyede aç. Gizlilik sorgusu useAuthorStory ile BİREBİR (private/closeFriends
// sızdırmaz). storyId ilk 10 içinde değilse 0'dan başlar.
async function openStoryFromUrl(
  username: string,
  storyId: string | null,
  { setModalData, setModalContent, pushedByAppRef, gezinmeSurumu: surum }: DerinBaglantiEylemleri,
) {
  // ⚠️ getUserByUsername KULLANILMIYOR: o fonksiyon `catch { }` ile hatayı
  // yutup null dönüyor (profileApi.ts:90) → "yok" ile "ulaşamadım" daha
  // çağrılmadan birleşiyor ve metadata da kayboluyor. Bilgiyi yok eden
  // kaynağa bağlanmak yerine snapshot DOĞRUDAN okunur.
  const cozumU = await derinBaglantiyiCoz(async () => {
    const canonical = String(username || "").trim().toLowerCase();
    const unameSnap = await getDoc(doc(db, "usernames", canonical));
    const okuma = docOkumasi(unameSnap);
    if (!okuma.varMi) return okuma;
    const uid = (unameSnap.data() as any)?.uid;
    if (!uid) return { varMi: false, sunucudan: okuma.sunucudan };
    const userSnap = await getDoc(doc(db, "users", uid));
    const uOkuma = docOkumasi(userSnap);
    return uOkuma.varMi
      ? { varMi: true, sunucudan: uOkuma.sunucudan, veri: { uid, ...(userSnap.data() as any) } }
      : uOkuma;
  });
  if (cozumU.durum !== "BULUNDU") {
    derinDurumGoster(cozumU.durum, { setModalData, setModalContent, pushedByAppRef, gezinmeSurumu: surum });
    return;
  }
  // #888 — kullanıcı bulundu ama arada gezinildiyse hikâye sorgusuna hiç girme.
  if (bayatCozum(surum)) return;
  {
    const user = cozumU.veri;
    const uid = user?.uid || user?.id;
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const qStories = query(
      collection(db, "hikayeler"),
      where("authorId", "==", uid),
      where("authorIsPrivate", "==", false),
      where("audience", "==", "public"),
      where("tarih", ">=", cutoff),
      limit(10),
    );
    const cozumS = await derinBaglantiyiCoz(async () => {
      const snap = await getDocs(qStories);
      return sorguOkumasi(snap, snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
    });
    if (cozumS.durum !== "BULUNDU") {
      derinDurumGoster(cozumS.durum, { setModalData, setModalContent, pushedByAppRef, gezinmeSurumu: surum });
      return;
    }
    // #888 — bkz. openPostFromUrl: geç dönen başarı da bayat olabilir.
    if (bayatCozum(surum)) return;
    // ⚠️ ÜRETİCİ BAŞKA DOSYADA (rapor edildi): `deepLinkResolve.DerinSonuc<T>`
    // ayrık birleşim DEĞİL — `veri?: T`. `durum === "BULUNDU"` dalında üretici
    // (`derinBaglantiyiCoz`) `veri`yi DAİMA dolduruyor ama tip bunu söylemiyor.
    // Guard yukarıda (`cozumS.durum !== "BULUNDU"` → return); ona atıflı `as`.
    // `sorguOkumasi`nın listesi `snap.docs.map(...)` → yalnız `id` okunuyor.
    const stories = cozumS.veri as Array<{ id?: string }>;
    let initialIndex = 0;
    if (storyId) {
      const i = stories.findIndex((s) => s.id === storyId);
      if (i >= 0) initialIndex = i;
    }
    setModalData({
      stories,
      kullaniciAdi: (user as any)?.kullaniciAdi || (user as any)?.username || username,
      profilFoto: (user as any)?.profilFoto || (user as any)?.avatar || "",
      initialIndex,
    });
    setModalContent(katman("viewingStory"));
    if (pushedByAppRef) pushedByAppRef.current = false;
  }
}

// ─── ÖLÜ ROTA KAPISI ─────────────────────────────────────────────────────────
//
// 🩺 ÖLÇÜLEN KUSUR (2026-08-31, 31 rezerve kelimenin tamamı tek tek çözüldü):
// `ROUTER_REZERVE`in 31 kelimesinin **23'ünün çıplak yolu hiçbir matcher'a
// düşmüyordu** — `/settings` · `/search` · `/admin` · `/creator` · `/place` ·
// `/sounds` · `/stories` · `/info` · `/u` · `/p` · `/c` … Çözücü `false`
// döndüğü için `activePage` DEĞİŞMİYOR, yani kullanıcı adres çubuğunda
// `/settings` yazarken **ana akışı** görüyor ve hiçbir hata da almıyordu.
//
// 🧬 SINIF: bu bir yazım hatası değil, **iki taraflı bir boşluk**. Kelime
// rezerve olduğu için `/{kullaniciAdi}` dalı onu ALMIYOR (doğru), ama kendi
// matcher'ı da YOK → ne sayfa açılıyor ne kullanıcı çözülüyor. Ve `firebase.json`
// bu kelimeleri özellikle SPA'ya boot ediyor (`/*` → `renderProfileShare`
// yutmasın diye) — yani yol gerçekten uygulamanın önüne geliyor.
//
// 🔧 KARAR: sessizce ana akışa düşmek ÜÇÜNCÜ ve YANLIŞ seçenektir — kullanıcıya
// yanlış ekranı doğruymuş gibi gösterir. Bir rezerve yol hiçbir matcher'a
// düşmüyorsa, **bilerek** bir "bulunamadı" hâline düşer. Hedef UYDURULMAZ
// (`/settings` → hangi ayar? `/admin` → hangi panel? ikisi de ürün kararıdır);
// yalnız yalan söylemek bırakılır.
//
// ⚠️ MUAFİYET tek kaynaktan gelir (`cozucuOncesiRotalar.ts`): `/auth/action`,
// `/card/{kod}` ve yasal slug'lar için çözücü `false` döner ama ekranı
// `App.tsx` erken `return` ile ZATEN veriyor — onların üstüne kart basmak
// çalışan üç sayfayı kırardı.
/** Yolun ilk segmenti (yoksa `""`). Yüzde-kodlama çözülür. */
function ilkSegment(path: string): string {
  const ham = String(path || "").split("?")[0].split("#")[0];
  const parca = ham.split("/")[1] || "";
  try {
    return decodeURIComponent(parca);
  } catch {
    return parca;
  }
}

/**
 * Bu yol REZERVE bir uygulama yoludur ama hiçbir matcher onu sahiplenmedi mi?
 *
 * ⚠️ SAF YÜKLEM — `resolvePathToPage`in SONUNDA çağrılır, yani buraya gelen her
 * yol zaten "hiçbir matcher tutmadı" demektir. Yüklem yalnız ikinci soruyu
 * cevaplar: *bu adres uygulamanın kendi ad alanına mı ait?* Cevap hayırsa
 * (`/aliveli`, `/rastgele-sey`) davranış DEĞİŞMEZ — orası çıplak profil
 * dalının işidir ve o dal `false` dönmeye devam eder.
 */
export function oluRotaMi(path: string): boolean {
  const seg1 = ilkSegment(path).toLowerCase();
  if (!seg1) return false;
  if (!ROUTER_REZERVE.includes(seg1)) return false;
  return !cozucuOncesiYolMu(path);
}

// ─── Path → sayfa/modal çözümleme ────────────────────────────────────────────
// PR-V3b — test için export edildi (runtime kullanımı DEĞİŞMEDİ; useDeepLink/
// usePopstate hâlâ modül-içi çağırır). appRouting.test.ts matcher'ları kilitler.
export function resolvePathToPage(path: string, actions: RotaEylemleri): boolean {
  const {
    setActivePage,
    setModalContent,
    setModalData,
    setProfileRouteUsername,
    pushedByAppRef,
    activePageRef,
  } = actions;
  // #888 — HER çözümleme bir gezinmedir. Sayaç burada (ve YALNIZ burada) artar;
  // bu çağrının kendi `replaceState`'leri sayacı etkilemez, dolayısıyla `?c=`
  // gibi "başlat, sonra URL'i temizle" dalları bayat sayılmaz.
  const buGezinme = ++_gezinmeSurumu;

  // IG paritesi — /p/{id} ve yorum permalinki /p/{id}/c/{commentId}[/r/{replyId}].
  const mPost = path.match(
    /^\/p\/([A-Za-z0-9_-]+)(?:\/c\/([A-Za-z0-9_-]+))?(?:\/r\/([A-Za-z0-9_-]+))?\/?$/,
  );
  // Yorum PANELİ yolu (`/p/{id}/comments`) — şema `gonderiYorumUrl.ts`te.
  // ÖLÇÜLDÜ (bu değişiklikten ÖNCE): bu yol hiçbir matcher'a düşmüyordu ve
  // `oluRotaMi` "p" rezerve olduğu için ÖLÜ ROTA kartı basıyordu
  // (`derinDurum:"yok", derinTur:"rota"`). Yani panelin makul adresi
  // kullanıcıya "bulunamadı" gösteriyordu.
  const mGonderiYorum = yoldanGonderiYorumPaneli(path);
  // PR-V3b — trailing-slash `\/?` eklendi (mPost ile hizalı). Öncesi `/c/{id}/`
  // hiçbir matcher'a düşmüyor, bare-profil "c" RESERVED olduğundan false →
  // klip sessizce home'a düşüyordu. Artık `/c/{id}` ve `/c/{id}/` aynı davranır.
  // URL BOŞLUĞU #3 — `/c/{id}/edit` (klip düzenleme sheet'i) COLD-LOAD'u.
  // Sheet'in kendisi geri getirilemez (tek açılış yolu ProfileView grid'i,
  // sahiplik + profil bağlamı soğuk açılışta yok) → dürüst davranış: KLİBİ
  // geri getir ve adres çubuğunun artık açık olmayan bir editörü iddia
  // etmesini bırak (`/c/{id}`'ye normalize). `/c/{id}` dalı DEĞİŞMEDİ.
  // IG paritesi — KLİP YORUM PERMALİNKİ `/c/{id}/c/{cid}[/r/{rid}]`, gönderinin
  // `mPost` deseninin EŞİ. Gönderide vardı, klipte YOKTU — oysa tartışma
  // klipte yaşıyor; paylaşılamayan yüzey tam olarak orasıydı. `/edit` eki
  // (soğuk-yük normalizasyonu) DEĞİŞMEDİ ve çapa alternatifiyle çakışmaz.
  const mClip = path.match(
    /^\/c\/([A-Za-z0-9_-]+)(?:\/c\/([A-Za-z0-9_-]+))?(?:\/r\/([A-Za-z0-9_-]+))?(?:\/edit)?\/?$/,
  );
  // PR-69 — `cards` alt-yolu: /u/{username}/cards VİTRİN PERMALINK'i profili
  // açar (followers/saved ile aynı yerleşik alt-yol deseni). PublicCardShowcase
  // deep-link'te (path /cards ile bitiyorsa) bölümü ziyaretçide boşken de
  // gösterir ("Henüz vitrine eklenmiş kart yok").
  // KART DETAYI — `/{u}/cards/{kod}` (detay modalı cold-load'u; `kartDetayUrl.ts`).
  //
  // ⚠️ ALTERNATİFİN YERİ BİLEREK SEÇİLDİ: `cards`ten ÖNCE yazmak daha okunur
  // olurdu ama `profilSekmeUrl.test.ts` kaynakta `followers|following|tagged|
  // saved|cards|clips` dizesini ARIYOR (matcher'dan bir sekmenin sessizce
  // düşmesini yakalayan kol). O diziyi bölmek, ilgisiz bir sözleşme testini
  // kırar. Bu yüzden yeni alternatif `repost`tan SONRA duruyor; regex
  // geri-izlemesi sırayı zaten kurtarıyor (ölçüldü: `/ali/cards/S1-007`,
  // `/ali/cards`, `/ali/cards/a/b` → sırasıyla eşleşir/eşleşir/eşleşmez).
  // Kod sınıfı `KART_DETAY_YOLU_DESENI` ile aynı; ikinci bir "kart kodu nedir"
  // tanımı YOK.
  const mProfile = path.match(
    /^\/u\/([^/]+)(?:\/(?:followers|following|tagged|saved|cards|clips|checkins|repost|cards\/[A-Za-z0-9_-]+|highlights\/[^/]+))?\/?$/,
  );
  // Anlatım yüzeyleri (`/info/star|gold|mycards|journey`) — şema TEK KAYNAK
  // `bilgiSayfasiUrl.ts`te; buraya regex KOPYALANMAZ.
  const mBilgi = yoldanBilgi(path);
  // Koleksiyon detayı (`/u/{sahip}/collections/{id}`) — şema TEK KAYNAK
  // `koleksiyonUrl.ts`te. `mProfile`den ÖNCE denenmeli: `/u/{param}` matcher'ı
  // bu yolu YUTMAZ (alt-yol alternasyonunda `collections` yok) ama sıra niyeti
  // gösterir ve alternasyon bir gün genişlerse sessizce kaymaz.
  const mKoleksiyon = yoldanKoleksiyon(path);
  const mExplore = path.match(/^\/explore(?:\/tags\/[^/]+)?\/?$/);
  // EMİR #76 — İlk Işıklar keşif galerisi (kendi URL'si; cold-load + paylaşım).
  const mDiscoveries = path.match(/^\/discoveries\/?$/);
  // Keşif sohbeti ipliği — DM'in `/direct/t/{id}` deseninin eşi.
  // `/discoveries` (galeri) matcher'ı TAM eşleşme olduğu için ÇAKIŞMA YOK.
  // Yeni bir rezerve kelime GEREKMEZ: "discoveries" zaten rezerve (ölçüldü —
  // `/discovery` olsaydı `firestore.rules` değişmeliydi, o dosya kilitli).
  const mDiscoveryThread = path.match(/^\/discoveries\/t\/([A-Za-z0-9_-]+)\/?$/);
  const mAdminReports = path.match(/^\/admin\/reports\/?$/);
  const mAdminCostDashboard = path.match(/^\/admin\/cost-dashboard\/?$/);
  const mCreatorDashboard = path.match(/^\/creator\/clips\/?$/);
  const mMyLive = path.match(/^\/mylive\/?$/);
  const mBlockedUsers = path.match(/^\/settings\/blocked\/?$/);
  // GİZLİ KELİMELER (IG paritesi) — `/settings/blocked` deseninin eşi.
  // TAM eşleşme; `/settings/…` altında başka bir matcher'la çakışmaz.
  const mGizliKelimeler = path.match(/^\/settings\/hidden-words\/?$/);
  // ÖNERİ SIFIRLAMA (IG "Reset suggested content") — aynı desen.
  const mOneriAyarlari = path.match(/^\/settings\/suggestions\/?$/);
  const mDirectThread = path.match(/^\/direct\/t\/([A-Za-z0-9_-]+)\/?$/);
  const mDirect = path.match(/^\/direct\/?$/);
  // PR-18 — IG paritesi: bildirimler kendi yolunu alır (/notifications).
  // ⚠️ TEK KAYNAK (`gorunumFiltreUrl.bildirimYolundaMi`): bu desen DÖRT
  // yerde ayrı ayrı yazılıydı (burası, aşağıdaki popstate kapısı,
  // `appModals` kapanış temizliği, `App.handleNavChange` push kapısı).
  // Dördü birebir aynıydı ama hiçbir mekanizma onları BİRLİKTE tutmuyordu —
  // "çürüyen liste" sınıfının uyuyan bir üyesi. Filtre parametresi (`?f=`)
  // `pathname`e DOKUNMADIĞI için dördü de değişmeden çalışır; birleştirme
  // yine de yapıldı ki bir sonraki tur yolu değiştirmek istediğinde
  // güncellenecek TEK yer olsun.
  const mNotifications = bildirimYolundaMi(path);
  const mPlace = path.match(/^\/place\/([^/]+)\/?$/);
  // IG paritesi — keşif overlay deep-link'leri (clips feed içinde açılır).
  // FeedContainerV3 mount'ta URL'den overlay'i kurar (parseDiscoveryFromUrl).
  const mSound = path.match(/^\/sounds\/([^/]+)\/?$/);
  const mClipsSearch = path.match(/^\/clips\/search\/?$/);
  // PR-V3e2a — MyClips ana feed base URL'si: /clips (+ /clips/ trailing). Şimdiye
  // dek adressizdi (bare /clips, "clips" RESERVED olduğu için profile düşmüyor →
  // home'a düşüyordu). Artık feed'i açar (discover default). Overlay AÇMAZ:
  // parseDiscoveryFromUrl("/clips") V3e1'de null → FeedContainerV3 sade feed mount
  // eder (kilitli dosyaya dokunulmaz). /clips/{tab|tags|trending|challenge} runtime
  // bağlama V3e4/V3e3/V3e5'e; back/forward V3e2b'ye bırakıldı.
  const mClips = path.match(/^\/clips\/?$/);
  // PR-V3e3 — hashtag cold-load: /clips/tags/{tag} → clips sayfası; FeedContainerV3
  // mount'ta parseDiscoveryFromUrl ile hashtag overlay'ini açar (sound/search deseni).
  const mClipsTags = path.match(/^\/clips\/tags\/([^/]+)\/?$/);
  // PR-V3e5 — trending leaderboard + challenge cold-load → clips sayfası; mount'ta
  // overlay açılır (section-özel trending + challenge). Bare /clips/trending → feed.
  const mClipsTrending = path.match(/^\/clips\/trending(?:\/[^/]+)?\/?$/);
  const mClipsChallenge = path.match(/^\/clips\/challenge\/([^/]+)\/?$/);
  // PR-V3e4a — feed tab cold-load: /clips/{following|neighborhood|saved} → clips
  // sayfası; FeedContainerV3 mount'ta tabFromUrl ile o tab'ı aktif eder (overlay
  // DEĞİL — activeTab modu, replaceState kuralı). Tab click/popstate sync V3e4b.
  const mClipsTab = path.match(/^\/clips\/(following|neighborhood|saved)\/?$/);
  const mMap = path.match(/^\/map\/?$/);
  // Composer/kamera + check-in (IG /create paritesi). Matcher `olusturUrl.ts`te —
  // burada satır-içi regex YOK: aynı şemayı iki yerde yazmak iki şema demektir.
  const olusturDurumu = yoldanOlustur(path);
  // IG paritesi — story cold-load: /stories/{username}/{storyId}.
  const mStory = path.match(/^\/stories\/([^/]+)(?:\/([^/]+))?\/?$/);
  // IG paritesi — bare profil: /{username}[/{sub}]. IG instagram.com/{username}
  // ile birebir. Eski /u/{username} (mProfile) da çalışır (geri uyum). Rezerve
  // kelimeler (diğer tüm üst-seviye rotalar) kullanıcı adı OLAMAZ — IG deseni.
  // KART DETAYI — `mProfile` ile BİREBİR aynı alternasyon (iki şema değil, bir
  // şema iki tabanda). Rezerve duvarı DEĞİŞMEZ: aşağıdaki `RESERVED.has(seg1)`
  // kapısı aynen çalışır → `/explore/cards/x` profil AÇMAZ (payda kolu testte).
  const mBareProfile = path.match(
    /^\/([^/]+)(?:\/(?:followers|following|tagged|saved|cards|clips|checkins|repost|cards\/[A-Za-z0-9_-]+|highlights\/[^/]+))?\/?$/,
  );

  // Query-param permalink: /c/{id} hosting rewrite renderClipShare
  // function'a gider (SPA boot ETMEZ), bu yüzden function gerçek kullanıcıyı
  // SPA-routed /?c={id}'ye yönlendirir → burada /c/{id} ile AYNI modal açılır.
  // (Aksi halde /?c= path'i "/" olarak çözülüp klip hiç açılmaz.) Aynı mantık
  // /?p={id} (post) için de. clipId/postId uzun (>=8) — kısa query'lerle çakışmaz.
  try {
    const sp = new URLSearchParams(
      (typeof window !== "undefined" && window.location.search) || "",
    );
    // `?go=` EN ÖNCE: bu bir içerik permalinki değil, ÇIPLAK PROFİL
    // rewrite'ının insan-yolu sarmalıdır (bkz. dosya başı). Adres çubuğunu
    // gerçek yola yaz, sonra o yolu normal çözümlemeye ver.
    const goHedef = goHedefiCoz(
      (typeof window !== "undefined" && window.location.search) || "",
    );
    if (goHedef && _goDerinlik === 0) {
      _goDerinlik += 1;
      try {
        // replace (push DEĞİL): interstitial + `?go=` girişi geçmişte KALMAZ →
        // geri tuşu kullanıcıyı paylaşım sarmalına geri sokmaz.
        window.history.replaceState(window.history.state, "", goHedef);
      } catch (_) { /* no-op */ }
      try {
        return resolvePathToPage(goHedef, actions);
      } finally {
        _goDerinlik = 0;
      }
    }
    const qClip = sp.get("c");
    if (qClip && /^[A-Za-z0-9_-]{8,40}$/.test(qClip)) {
      // Klip yorum çapası — `renderClipShare` `/c/{id}/c/{cid}[/r/{rid}]`
      // yolunu `?c=&cm=&rp=` olarak yollar (gönderiyle AYNI anahtarlar).
      const cmClip = sp.get("cm");
      const rpClip = sp.get("rp");
      openClipFromUrl(qClip, {
        setModalData,
        setModalContent,
        setActivePage,
        pushedByAppRef,
        gezinmeSurumu: buGezinme,
        toFeed: true,
        feedMountlu: activePageRef?.current === "clips",
        commentId: cmClip && /^[A-Za-z0-9_-]{1,40}$/.test(cmClip) ? cmClip : null,
        replyId: rpClip && /^[A-Za-z0-9_-]{1,40}$/.test(rpClip) ? rpClip : null,
      });
      // ANDROID GERİ-TUŞU FIX: ?c= query'sini temizle — aksi halde donanım geri
      // tuşu popstate'te resolvePathToPage'i tekrar çalıştırıp AYNI modalı
      // yeniden açıyordu (kapanmak yerine döngü). /c/{id} path'iyle aynı davranış.
      try { window.history.replaceState({}, "", "/"); } catch (_) {}
      return true;
    }
    // EMİR #79 — /discoveries paylaşım-cold-redirect'i: renderDiscoveriesShare insanı
    // /?discover=1'e yollar (path /discoveries fonksiyona gider). İlk Işıklar galerisini aç.
    if (sp.get("discover") === "1") {
      setActivePage("discoveries");
      try { window.history.replaceState({}, "", "/discoveries"); } catch (_) {}
      return true;
    }
    const qPost = sp.get("p");
    if (qPost && /^[A-Za-z0-9_-]{8,40}$/.test(qPost)) {
      // Yorum permalinki cold-redirect'i: renderPostShare insanı /?p={id}&cm={cid}
      // [&rp={rid}]'ye yollar (path /p/{id}/c/{cid} fonksiyona gider). cm/rp oku →
      // post + yorum anchor'ı aç. ?c= (klip) ile çakışmasın diye cm/rp anahtarları.
      const cm = sp.get("cm");
      const rp = sp.get("rp");
      const cmGecerli = cm && /^[A-Za-z0-9_-]{1,40}$/.test(cm) ? cm : null;
      const rpGecerli = rp && /^[A-Za-z0-9_-]{1,40}$/.test(rp) ? rp : null;
      // YORUM PANELİ soğuk yükü. `/p/{id}/comments` hosting'de `renderPostShare`
      // fonksiyonuna gider (path SPA'ya HİÇ gelmez) ve o fonksiyon insanı
      // `/?p={id}&yorumlar=1`e yollar. Bayrağı burada okumazsak `/comments`
      // niyeti zincirin bu halkasında sessizce düşerdi — matcher'ın tek
      // başına soğuk yükü VERMEDİĞİ ölçüldü (`gonderiYorumUrl.ts` başlığı).
      // Çapa VARSA yok sayılır: `/comments` şekli çapa taşımaz, ikisi birden
      // gelmişse kaynak `cm` olduğu için çapalı permalink daha spesifiktir.
      const yorumOdakli = !cmGecerli && sp.get(GONDERI_YORUM_SORGUSU) === "1";
      openPostFromUrl(qPost, {
        setModalData,
        setModalContent,
        pushedByAppRef,
        gezinmeSurumu: buGezinme,
        commentId: cmGecerli,
        replyId: rpGecerli,
        yorumOdak: yorumOdakli,
      });
      // 🔴 ESKİDEN BURADA `replaceState({}, "", "/")` VARDI ve ÖLÇÜLEN ZARAR
      // şuydu: paylaşılan bir gönderi linki açıldığında içerik geliyor ama
      // ADRES ÇUBUĞU `/` oluyordu. O an adres çubuğunu kopyalayan kullanıcı
      // gönderiyi değil ANA SAYFAYI paylaşıyordu — yani paylaşım zinciri
      // ikinci elde kopuyordu.
      //
      // Sorgu (`?p=`) yine temizlenmeli — gerekçesi 724-726'daki ANDROID
      // GERİ-TUŞU FIX'i: popstate'te `resolvePathToPage` sorguyu yeniden
      // görüp AYNI modalı yeniden açıyordu. Ama temizliğin hedefi `/`
      // olmak ZORUNDA DEĞİL: doğru desen bu dosyada zaten var — `?go=`
      // kolu (yukarısı) sorguyu GERÇEK YOLLA değiştiriyor. Aynısını
      // gönderiye uyguluyoruz: sorgu gider, gönderi kimliği KALIR.
      //
      // Geri-tuşu davranışı DEĞİŞMEZ: bu bir `replaceState` (yeni geçmiş
      // girdisi YOK). Soğuk yükte geçmişte tek girdi vardır; geri tuşu
      // önceki hâlde de bu hâlde de siteden çıkar. Fark yalnız o tek
      // girdinin ADRESİ: `/` yerine `/p/{id}`.
      // Adres, geldiği ŞEKLE geri kurulur — paylaşım zinciri ikinci elde de
      // kopmasın diye (aşağıdaki blok). Yorum paneli yolu da bu kurala dâhil:
      // aksi halde `/p/{id}/comments` paylaşan kullanıcı, açtığında adres
      // çubuğunda `/p/{id}` görür ve panelin adresi TEK KULLANIMLIK olurdu.
      let gercekYol = `/p/${qPost}`;
      if (cmGecerli) {
        gercekYol += `/c/${cmGecerli}`;
        if (rpGecerli) gercekYol += `/r/${rpGecerli}`;
      } else if (yorumOdakli) {
        gercekYol = gonderiYorumPaneliYolu(qPost) || gercekYol;
      }
      try { window.history.replaceState({}, "", gercekYol); } catch (_) {}
      return true;
    }
  } catch {
    /* no-op — query yoksa normal path çözümlemesine devam */
  }

  // ─── Oluşturma katmanı (composer/kamera/check-in) ───────────────────────
  // Cold-load: `/create`, `/create/post`, `/create/clip`, `/create/camera`,
  // `/create/checkin`, `/create/checkin/{placeId}`. Hepsi MODAL açar; alttaki
  // `activePage` DEĞİŞTİRİLMEZ (composer bir katman, bir sayfa değil) — bu
  // sayede geri tuşuyla kapandığında altta ne varsa o kalır.
  if (olusturDurumu) {
    if (olusturDurumu.modal === "newCheckInDetail") {
      // Mekân yükü (isim/adres) URL'de YOK — `history.state`te yaşar ve
      // YENİLEMEDE de aynı history girişinde kalır. Yabancı sekmede açılan
      // paylaşım linkinde state yoktur → yalnız id geçilir; isim UYDURULMAZ
      // (#783 dersi: id/koordinat isim yerine geçmez).
      const mekan = mekaniStatedenOku(
        typeof window !== "undefined" ? window.history.state : null,
      );
      const idUyuyor = !!mekan && (!mekan.id || mekan.id === olusturDurumu.placeId);
      setModalData(idUyuyor ? mekan : { id: olusturDurumu.placeId });
      setModalContent(katman("newCheckInDetail"));
      return true;
    }
    // `kamera` YALNIZ `/create/camera`da true → UploadComposerV3 kamerayla açılır.
    setModalData(olusturDurumu.kamera ? { kamera: true } : null);
    setModalContent(olusturDurumu.modal);
    return true;
  }
  // GÖNDERİ YORUM PANELİ — `/p/{id}/comments` (çapasız yorum katmanının adresi).
  // `mPost`ten ÖNCE: `mPost` bu şekli bugün YUTMUYOR (ölçüldü) ama sıra niyeti
  // gösterir ve o desen bir gün genişlerse sessizce kaymaz.
  if (mGonderiYorum) {
    openPostFromUrl(mGonderiYorum, {
      setModalData,
      setModalContent,
      pushedByAppRef,
      gezinmeSurumu: buGezinme,
      yorumOdak: true,
    });
    return true;
  }
  if (mPost) {
    openPostFromUrl(mPost[1], {
      setModalData,
      setModalContent,
      pushedByAppRef,
      gezinmeSurumu: buGezinme,
      commentId: mPost[2] || null,
      replyId: mPost[3] || null,
    });
    return true;
  }
  if (mClip) {
    // `/c/{id}/edit` → `/c/{id}` normalize (yalnız `/edit` eki düşer; clipId
    // KORUNUR, yani EMİR #94.4'ün "URL'i silme" kuralı ihlal edilmez).
    const normalize = klipDuzenleSogukYukKarari(path).normalizeHedefi;
    if (normalize) {
      try { window.history.replaceState(window.history.state, "", normalize); } catch (_) {}
    }
    openClipFromUrl(mClip[1], {
      setModalData,
      setModalContent,
      setActivePage,
      pushedByAppRef,
      gezinmeSurumu: buGezinme,
      // P0-1 — paylaşılan/soğuk-yüklenen `/c/{id}` de V3 tam ekranda açılır.
      // `?c=` yolu bunu zaten yapıyordu; iki paylaşım yüzeyi AYNI URL için
      // FARKLI ekran gösteriyordu (denetim §3.2 · sahne 58).
      toFeed: true,
      feedMountlu: activePageRef?.current === "clips",
      commentId: mClip[2] || null,
      replyId: mClip[3] || null,
    });
    return true;
  }
  // ANLATIM YÜZEYLERİ — `/info/{konu}`: yıldız / Gold / MyCards / yolculuk.
  // "MyLASA yıldızı nedir?" sorusuna artık LİNK atılabilir. İki segment →
  // çıplak profil matcher'ıyla ÇAKIŞMAZ; ayrıca "info" ROUTER_REZERVE'de
  // (zaten KAYIT_BLOCKLIST'te olduğu için rules/server kopyası DEĞİŞMEDİ).
  //
  // Sheet'ler App ağacına DEĞİL, `openStarInfo` ile aynı global lazy-host'a
  // mount olur (ToastBoot deseni) → router'ın modal kütüğüne yeni bir katman
  // eklemeye gerek yok ve profil dışındaki her yerden (soğuk yük dâhil)
  // açılabilirler. Dinamik import: çözücü, anlatım sheet'lerinin ağırlığını
  // her gezinmede taşımasın.
  if (mBilgi) {
    import("../components/infoSheetHost")
      .then((m) => m.bilgiSheetiAc(mBilgi))
      .catch(() => { /* anlatım yüzeyi kritik değil — sayfa yine çalışır */ });
    return true;
  }
  // KOLEKSİYON DETAYI soğuk yükü — profil dalından ÖNCE.
  //
  // Sahip parametresi UID VEYA kullanıcı adı olabilir (yazıcı UID yazar, ama
  // elle yazılmış/eski bir link kullanıcı adı taşıyabilir). UID'ye çevirme
  // ASENKRON olduğu için `openStoryFromUrl`in yaptığı gibi ateşle-unut:
  // matcher SENKRON `true` döner (yol tanındı), sheet çözüm gelince açılır.
  if (mKoleksiyon) {
    koleksiyonDetayiniAc(mKoleksiyon.ownerParam, mKoleksiyon.collectionId, buGezinme);
    return true;
  }
  if (mProfile) {
    // /u/:param — param kullanıcı adı VEYA ham uid olabilir (V3 feed adres
    // çubuğuna /u/<uid> push'luyor). Çözümleme Profile.tsx'te: önce username,
    // boş dönerse uid fallback (looksLikeUid). Burada param'ı olduğu gibi geçir.
    setProfileRouteUsername(decodeURIComponent(mProfile[1]));
    setActivePage("profile");
    return true;
  }
  if (mAdminReports) {
    setActivePage("adminReports");
    return true;
  }
  if (mAdminCostDashboard) {
    setActivePage("adminCostDashboard");
    return true;
  }
  if (mCreatorDashboard) {
    setActivePage("creatorDashboard");
    return true;
  }
  if (mExplore) {
    setActivePage("explore");
    return true;
  }
  // Keşif sohbeti cold-load'u — DM'in mDirectThread dalının eşi: keşif
  // modalını aç ve seed'i ver. Galeri dalından ÖNCE denenir (iki matcher
  // örtüşmese de sıra niyeti gösterir).
  if (mDiscoveryThread) {
    setModalContent(katman("discovery"));
    setModalData({ matchId: mDiscoveryThread[1] });
    return true;
  }
  // EMİR #76 — /discoveries: İlk Işıklar galerisi (tam sayfa; /explore deseni).
  if (mDiscoveries) {
    setActivePage("discoveries");
    return true;
  }
  if (mMyLive) {
    setActivePage("mylive");
    return true;
  }
  if (mBlockedUsers) {
    setActivePage("blockedUsers");
    return true;
  }
  if (mGizliKelimeler) {
    setActivePage("gizliKelimeler");
    return true;
  }
  if (mOneriAyarlari) {
    setActivePage("oneriAyarlari");
    return true;
  }
  // DM deep-link: gelen kutusu + belirli sohbet (modal). conversationId
  // DmInbox'a modalData ile geçer; karşı taraf id'den türetilir.
  if (mDirectThread) {
    setModalContent(katman("messages"));
    setModalData({ conversationId: mDirectThread[1] });
    return true;
  }
  if (mDirect) {
    setModalContent(katman("messages"));
    setModalData(null);
    return true;
  }
  // PR-18 — /notifications cold-load: bildirimler modalını aç (DM /direct deseni).
  if (mNotifications) {
    setModalContent(katman("notifications"));
    setModalData(null);
    return true;
  }
  // Mekan deep-link (modal). Soğuk açılışta yalnız placeId var (isim/koordinat
  // yok → kullanıcı onayladı: in-app işlevsel, soğuk linkte isim eksik olabilir).
  if (mPlace) {
    setModalContent(katman("placeDetail"));
    setModalData({ placeId: decodeURIComponent(mPlace[1]) });
    return true;
  }
  // /sounds/{id} ve /clips/search → clips sayfası; FeedContainerV3 URL'den overlay'i
  // mount'ta açar (discoveryView init = parseDiscoveryFromUrl). PR-V3e2a — bare
  // /clips de aynı sayfayı açar (overlay YOK → discover default); /sounds ve
  // /clips/search davranışı DEĞİŞMEZ. PR-V3e3 — /clips/tags/{tag} de clips'i açar
  // (mount'ta hashtag overlay). PR-V3e5 — /clips/trending[/{section}] ve
  // /clips/challenge/{id} de clips'i açar (mount'ta trending/challenge overlay).
  // PR-V3e4a — /clips/{following|neighborhood|saved} de clips'i açar (mount'ta o tab).
  if (mSound || mClipsSearch || mClips || mClipsTags || mClipsTrending || mClipsChallenge || mClipsTab) {
    setActivePage("clips");
    return true;
  }
  // IG: harita kendi bölüm yolunu alır (/explore, /direct gibi).
  if (mMap) {
    setActivePage("map");
    return true;
  }
  // IG paritesi — story cold-load: /stories/{username}/{storyId} → viewer aç.
  if (mStory) {
    openStoryFromUrl(
      decodeURIComponent(mStory[1]),
      mStory[2] ? decodeURIComponent(mStory[2]) : null,
      { setModalData, setModalContent, pushedByAppRef, gezinmeSurumu: buGezinme },
    );
    return true;
  }
  // IG paritesi — bare profil EN SON (tüm rezerve rotalardan sonra): /{username}.
  // Rezerve kelimeler kullanıcı adı sayılmaz (çakışmayı önler).
  // EMİR #96.3 — TEK KAYNAK: liste artık src/constants/reservedUsernames.ts'te.
  // Router'ın rezerve rotaları ile kayıt blocklist'i AYRI listelerde yaşayınca
  // sapmıştı; birleşim orada yapılıyor (ROUTER_REZERVE ⊆ TUM_REZERVE).
  const RESERVED = new Set<string>(ROUTER_REZERVE);
  if (mBareProfile) {
    const seg1 = decodeURIComponent(mBareProfile[1]);
    if (!RESERVED.has(seg1.toLowerCase())) {
      setProfileRouteUsername(seg1);
      setActivePage("profile");
      return true;
    }
  }
  // ── ÖLÜ ROTA: rezerve ad alanı, sahipsiz yol (bkz. yukarıdaki «ÖLÜ ROTA
  // KAPISI» bloğu). Buraya gelen yol hiçbir matcher'a düşmedi VE çıplak profil
  // dalı da onu almadı (rezerve). Sessizce `false` dönmek "ana akışı göster"
  // demekti; artık ne olduğu SÖYLENİR.
  //
  // `true` DÖNÜLÜR ve bu bilerek: yol TANINDI (uygulamanın ad alanına ait),
  // yalnız karşılığı yok. `false` dönseydi (a) `derinBaglantiyiYenidenDene`
  // son çare olarak `location.reload()` yapardı — ölü bir rotada yenilemek
  // aynı ölü rotayı getirir, yani hatayı hatayla karşılamak olurdu (#94.3);
  // (b) popstate'teki `!resolved` dalı kartı doğduğu turda kapatırdı.
  if (oluRotaMi(path)) {
    setModalData({ derinDurum: "yok", derinTur: "rota" });
    setModalContent(katman("derinBaglantiDurumu"));
    if (pushedByAppRef) pushedByAppRef.current = false;
    return true;
  }
  return false;
}

/**
 * #888 — «Tekrar dene» AYNI SEKMEDE, SPA İÇİNDE.
 *
 * Eski davranış tek çareydi: `window.location.reload()`. Tam yenileme
 * bütün uygulama durumunu (feed konumu, oynatma, taslak, açık sekme) yakar
 * ve ağ gerçekten kopuksa boot da başarısız olur — yani hatayı hatayla
 * karşılar (#94.3 kuralının aynısı). Artık önce MEVCUT yol yeniden çözülür;
 * yalnız çözücü o yolu hiç tanımıyorsa (`false`) son çare olarak yenilenir.
 *
 * @returns SPA içinde yeniden çözüldüyse `true`, tam yenilemeye düşüldüyse `false`.
 */
export function derinBaglantiyiYenidenDene(actions: RotaEylemleri): boolean {
  // Kartı ÖNCE düşür: aksi halde çözücü asenkron dalda hiçbir şey yazmaz ve
  // kullanıcı "tekrar dene"ye bastığında ekranda hiçbir değişiklik görmez.
  actions.setModalContent(null);
  actions.setModalData(null);
  const yol =
    (typeof window !== "undefined" && window.location && window.location.pathname) || "/";
  const cozuldu = resolvePathToPage(yol, actions);
  if (cozuldu) return true;
  // Çözücü bu yolu tanımıyor → SPA içinde yapılabilecek bir şey kalmadı.
  try { window.location.reload(); } catch { /* no-op */ }
  return false;
}

// ─── useDeepLinkHandler — mount'ta URL'den sayfa/modal aç ────────────────────
export function useDeepLinkHandler({
  loading,
  user,
  setActivePage,
  setModalContent,
  setModalData,
  setProfileRouteUsername,
  pushedByAppRef,
  activePageRef,
}: RotaEylemleri & {
  loading?: boolean;
  // Gövde `user`ı YALNIZ doğruluk testinde kullanıyor (`!user`) → `unknown`.
  user?: unknown;
}) {
  useEffect(() => {
    if (loading || !user) return;
    const path = window.location.pathname;
    resolvePathToPage(path, {
      setActivePage,
      setModalContent,
      setModalData,
      setProfileRouteUsername,
      pushedByAppRef,
      activePageRef,
    });
  }, [loading, user]); // eslint-disable-line react-hooks/exhaustive-deps
}

// ─── usePopstateHandler — geri/ileri tuşu ────────────────────────────────────
export function usePopstateHandler({
  modalContent,
  modalReturnRef,
  setActivePage,
  setModalContent,
  setModalData,
  setProfileRouteUsername,
  pushedByAppRef,
  activePageRef,
}: RotaEylemleri & {
  modalContent?: ModalIcerigi;
  modalReturnRef?: { current: ModalDonusDurumu | null } | null;
  // NOT: `activePageRef` burada YENİDEN tanımlanmıyor — P0-1'de ortak
  // `RotaEylemleri` sözleşmesine taşındı (aynı alanın iki tanımı, kanonun
  // *çürüyen liste* dersinin bir başka hâliydi: biri güncellenip öteki
  // unutulabilirdi). Gerekçesi ve #888 bağlamı orada yazılı.
}) {
  useEffect(() => {
    const onPop = async () => {
      // Geçici overlay (BottomSheet) görünürken gelen pop = overlay kapatma;
      // sayfa yeniden-çözme YAPMA. Aksi halde harita gibi kendi URL'si olmayan
      // sekmelerde bayat "/u/<kullanıcı>" URL'si profil sayfasını açıyordu.
      if (isSheetRouteActive()) return;

      // DM modalı açıkken pop'ları DmInbox kendi yönetir (/direct ↔ /direct/t
      // senkronu + çıkışta kapat). Global router karışmasın.
      if (modalContent === "messages") return;

      // PR-18 — Bildirimler modalı açıkken pop geldi ve URL artık /notifications
      // değil → modalı kapat; altındaki path normal çözülmeye devam etsin
      // (ör. /explore'a geri dönüşte explore restore edilir). İleri tuşuyla
      // /notifications'a dönüş aşağıdaki resolvePathToPage ile yeniden açar.
      if (
        modalContent === "notifications" &&
        !bildirimYolundaMi(window.location.pathname)
      ) {
        setModalContent(null);
        setModalData(null);
        if (pushedByAppRef) pushedByAppRef.current = false;
      }

      // ── P0-6 · GERİ TUŞU DA KAPANIŞ KAPISINDAN GEÇER ─────────────────────
      // ÖLÇÜLEN KUSUR: `NewPost` kapanışı tek kapıdan (`kapatmayiIste`) geçip
      // içerik varsa onay soruyordu; Escape ve ✕ o kapıdan geçiyordu ama
      // TARAYICI GERİ'Sİ geçmiyordu. `writeDraft` medyayı saklamadığı için
      // (`NewPost.tsx:125`) seçilen fotoğraflar SESSİZCE gidiyordu. Ölçüm
      // (`src/gonderiGeriTusuTaslakKapisi.render.test.tsx`, düzeltmeden önce):
      //     onay=0 · composer=YOK · küçük resim 1 → 0 · yol=/
      //
      // ⚠️ BU BLOK, AŞAĞIDAKİ «oluşturma katmanı» DALINDAN ÖNCE OLMAK ZORUNDA.
      // Kusurun İKİ iniş yolu var, o dal yalnız birini görüyor:
      //   (A) GERİ create-DIŞI bir yola düşer (`/`) → o dal kapatır.
      //   (B) GERİ `/create`e düşer (menüden açma = en yaygın akış) → o dal
      //       ATLANIR, `resolvePathToPage` MENÜYÜ açar ve `NewPost` unmount
      //       olur. Kapı aşağıda olsaydı (B) yolunda kusur SÜRERDİ.
      //
      // Soru BURADA sorulmaz: metin ve "kaybolacak bir şey var mı" kararı
      // composer'ın state'ine bağlı → `services/gonderiKapanisKapisi` üzerinden
      // SORULUR (emsal: `isPostUploading()` aynı biçimde okunan bir kapı).
      if (modalContent === "newpost") {
        // Yükleme kalkanı ÖNCE ve SORUSUZ: sürerken geri tuşu composer'ı
        // kapatamaz (arka planda devam eden upload "hayalet post" üretir).
        // Bu kontrol eskiden AŞAĞIDAKİ dalın içindeydi → (B) yolunda kalkan da
        // YOKTU; yukarı taşınması onu iki iniş yoluna birden yayar.
        if (isPostUploading() || !gonderiKapanabilirMi()) {
          // Pop'u geri al → kullanıcı composer'ın URL'sinde VE içeriğiyle kalır.
          try {
            const geri = olusturYolu({ modal: "newpost" });
            if (geri) window.history.pushState(window.history.state, "", geri);
          } catch (_) { /* no-op */ }
          return;
        }
      }

      // Oluşturma katmanı (composer/kamera/check-in) açıkken GERİ geldi ve URL
      // artık bir `/create…` yolu DEĞİL → katmanı kapat. Bildirimler dalının
      // eşi. URL HÂLÂ `/create…` ise (ör. `/create/clip` → `/create`) kapatma
      // YAPILMAZ: aşağıdaki `resolvePathToPage` bir üst adımı (menüyü) açar.
      if (
        olusturModaliMi(modalContent) &&
        !olusturYolundaMi(window.location.pathname)
      ) {
        // (Yükleme kalkanı + taslak onayı YUKARI taşındı — bkz. P0-6 bloğu.)
        setModalContent(null);
        setModalData(null);
        if (pushedByAppRef) pushedByAppRef.current = false;
      }

      // Profil modalı (başka kullanıcı) açıkken GERİ = modalı kapat + önceki
      // durumu (ör. yorum modalı) restore et. URL zaten pop ile önceki sayfaya
      // döndü; profil modalı mevcut sayfanın ÜSTÜNDE açıldığından activePage
      // DEĞİŞMEDİ → sayfa yeniden-çözme GEREKMEZ (resolvePathToPage atlanır).
      if (
        modalContent === "viewingProfile" ||
        modalContent === "placeDetail" ||
        modalContent === "viewingStory"
      ) {
        const ret = modalReturnRef?.current || null;
        if (modalReturnRef) modalReturnRef.current = null;
        setModalContent(ret?.modalContent || null);
        setModalData(ret?.modalData || null);
        // Altdaki modalın orijinal push-bayrağını GERİ VER (false'a zorlama —
        // aksi halde push'lu permalink modalının kapanışı bozulur).
        if (pushedByAppRef) pushedByAppRef.current = !!ret?.pushedByApp;
        return;
      }

      const path = window.location.pathname;
      // #837 — ÇÖZÜCÜNÜN NE YAPTIĞINI ÖLÇ (varsayma). Kapanış kararı, yeni
      // yolun bir SAYFAYA mı yoksa bir MODALA mı çözüldüğüne bağlı; bunu
      // `resolvePathToPage`'in dallarını burada KOPYALAYARAK değil, ona
      // geçtiğimiz eylemleri sarmalayıp GÖZLEYEREK öğreniyoruz — tek kaynak
      // çözücünün kendisi kalır (ikinci bir "hangi yol modal açar" listesi YOK).
      let cozucuSayfaYazdi = false;
      let cozucuModalYazdi = false;
      const resolved = resolvePathToPage(path, {
        setActivePage: (p) => { cozucuSayfaYazdi = true; setActivePage(p); },
        setModalContent: (c) => { cozucuModalYazdi = true; setModalContent(c); },
        setModalData,
        setProfileRouteUsername,
        pushedByAppRef,
        activePageRef,
      });
      // Yeni yol bir SAYFAYA çözüldü ve hiçbir modal AÇMADI → üstte duran
      // URL-katmanının altında artık onu taşıyan URL yok → kapanmalı.
      // (Kusurun `/explore` varyantı: `/p/{id}` katmanı silinmiş içerik kartına
      // düşer, GERİ ile `/explore`e dönülür — `resolved` TRUE olduğu için eski
      // kod katmanı ekranda bırakıyordu. Aynı kusur, farklı iniş yolu.)
      //
      // ⚠️ Modal AÇAN yollar (/place, /direct, /create…) bu daldan MUAF:
      // çözücü modal state'inin sahibidir, ona dokunmak "açılanı aynı turda
      // kapatma" regresyonudur (naif düzeltme tuzağı — ölçüldü).
      // ⚠️ Async açıcılar (/p/{id}, /c/{id}) senkron HİÇBİR ŞEY yazmaz → iki
      // bayrak da false → bu dal ÇALIŞMAZ; katman, açıcı gelene kadar yerinde
      // kalır (mevcut davranış korunur).
      const sayfayaCozuldu = cozucuSayfaYazdi && !cozucuModalYazdi;
      // ── #888 — ASENKRON YOLLARIN KÖR NOKTASI ────────────────────────────
      // Yukarıdaki uyarı bu kusuru zaten TARİF EDİYORDU ama sonucunu kabul
      // etmişti: *"Async açıcılar (/p/{id}, /c/{id}) senkron HİÇBİR ŞEY
      // yazmaz → katman, açıcı gelene kadar yerinde kalır."* Bekleyen katman
      // `viewingClip`/`viewingComments` için MEŞRUDUR (altta gerçek içerik
      // var, sadece tazeleniyor). Ama «Bağlantı kurulamadı» DURUM KARTI için
      // DEĞİL: o kart, kendisini doğuran YOLA aittir; başka bir yola
      // gezinildiği anda BAYATTIR ve yeni yolun ekranını taklit eder.
      //
      // ÖLÇÜLDÜ (9 turluk koşu, tohum 3): `/discoveries` · `/p/{id}` · `/`
      // duraklarının hepsi BİREBİR aynı ekranı verdi ve arıza kalkmadığı
      // için tur boyunca kilitli kaldı — tek çıkış `location.reload()`.
      //
      // ⚠️ ÇÖZÜCÜ MODAL AÇTIYSA DOKUNULMAZ (`!cozucuModalYazdi`): `/place`,
      // `/direct`, `/create…` gibi yollarda kapatmak "açılanı aynı turda
      // kapatma" regresyonudur — `geriTusuKatmanKapanisi.test.tsx`in B kolu
      // bunu sözleşme olarak kilitliyor.
      // ⚠️ Bu blok `!resolved || sayfayaCozuldu` dalının DIŞINDA: asenkron
      // yolda ikisi de false'tur, kusurun yaşadığı yer TAM OLARAK burasıdır.
      const bayatDurumKarti =
        modalContent === "derinBaglantiDurumu" && resolved && !cozucuModalYazdi && !sayfayaCozuldu;
      if (bayatDurumKarti) {
        setModalContent(null);
        setModalData(null);
        if (pushedByAppRef) pushedByAppRef.current = false;
      }
      if (!resolved || sayfayaCozuldu) {
        // #837 — ÇÜRÜYEN LİSTE KALDIRILDI. Eskiden burada elle yazılmış
        // `["viewingComments","viewingClip"]` vardı; `derinBaglantiDurumu` ve
        // `discovery` o listeye hiç eklenmediği için "URL değişti ama katman
        // ekranda kaldı" kusuru doğuyordu. Karar artık ROUTER_MODAL_KATMANLARI
        // kütüğünden TÜRETİLİYOR (bkz. dosya başı) — kütüğe yazılmayan bir ad
        // zaten derlenmiyor, dolayısıyla liste sessizce çürüyemez.
        //
        // ⚠️ Kapanış BİLEREK `!resolved` İÇİNDE: `resolvePathToPage` popstate'te
        // modal AÇAN yoldur. Dışarı alınırsa (ölçüldü) açılan modal aynı turda
        // kapatılır → ileri tuşu kırılır. `modalContent` burada çözücüden ÖNCEKİ
        // değerdir (React setter'ı closure değişkenini değiştirmez).
        if (urlKatmaniModaliMi(modalContent)) {
          setModalContent(null);
          setModalData(null);
          if (pushedByAppRef) pushedByAppRef.current = false;
          // ── #888 — KATMAN KAPANDI AMA ALTTAKİ SAYFA BAYAT ─────────────
          // ÖLÇÜLDÜ (sanal dünya, tohum 3): aynı turda `/` bir kez `ana=14`,
          // bir kez `ana=0`. İki farklı EKRAN, tek URL etiketi. Sebep:
          // `/discoveries` gezildikten sonra `/p/{id}` katmanı köke GERİ ile
          // kapanıyor, ama `setActivePage("home")` dalı bu `else if`in ÖTEKİ
          // kolunda olduğu için HİÇ çalışmıyordu → adres `/`, ekran hâlâ
          // (boş) keşif galerisi.
          //
          // ⚠️ KÖRLEMESİNE "home" YAZILMAZ: `/` hem ana akışın hem MyClips
          // beslemesinin taban yoludur (App.handleNavChange: home|clips → "/").
          // Beslemedeki kullanıcıyı home'a atmak gerçek bir regresyondur ve
          // `geriTusuKatmanKapanisi.test.tsx` bunu sözleşme olarak kilitliyor.
          // Bu yüzden sıfırlama YALNIZ kökte OLMAYAN kendi URL'sine sahip
          // sayfalar için yapılır (`KOK_YOLU_OLMAYAN_SAYFALAR`).
          if (!resolved && (path === "/" || path === "")) {
            const bayatSayfa = activePageRef?.current;
            if (bayatSayfa && KOK_YOLU_OLMAYAN_SAYFALAR.has(bayatSayfa)) setActivePage("home");
          }
        } else if (!resolved && (path === "/" || path === "")) {
          // Kendi URL'si olan aktif-sayfadan (/map, /explore, /{profil}) köke
          // GERİ dönüldü → resolvePathToPage("/") false döner; activePage'i home'a
          // sıfırla. Aksi halde URL "/" ama sayfa (ör. harita) bayat kalıyordu.
          // `!resolved` şartı AÇIKÇA yazıldı: dış koşul artık `sayfayaCozuldu`
          // ile de girilebiliyor ve o durumda çözücü activePage'i ZATEN yazdı —
          // üstüne "home" yazmak kullanıcıyı çözdüğü sayfadan atardı.
          //
          // 🔴 ÖLÇÜLDÜ (bu tur, sonda ile): `modalContent=null` +
          // `activePage="clips"` + GERİ→`/` bu dala düşüyor ve `["home"]`
          // yazıyordu. Yani BESLEMEDEKİ kullanıcı, kök yola dönen HERHANGİ bir
          // geri hareketinde sekmesinden atılıyordu. Bu, 8 satır YUKARIDAKİ
          // kardeş dalın yorumunun AÇIKÇA YASAKLADIĞI şeydir (*"KÖRLEMESİNE
          // 'home' YAZILMAZ … Beslemedeki kullanıcıyı home'a atmak gerçek bir
          // regresyondur"*) — ama guard yalnız O dala konmuştu; bu dal, aynı
          // kuralın kapsanmamış ikizi olarak kalmıştı (#837'nin *çürüyen
          // liste* dersinin bir başka hâli: kural yazıldı, iki koldan birine
          // uygulandı).
          //
          // Guard NEGATİF kümeyle yazıldı (`KOK_TABANLI_SAYFALAR`), pozitif
          // `KOK_YOLU_OLMAYAN_SAYFALAR` ile DEĞİL: `activePageRef` opsiyoneldir
          // ve verilmediğinde pozitif küme guard'ı hiç ateşlemez → `/map`,
          // `/explore` için mevcut sıfırlama SESSİZCE ölürdü. Negatif kümede
          // ref yokluğu eski davranışı (home yaz) korur; yalnız kökü taban
          // kabul eden iki sekme muaf tutulur.
          const aktif = activePageRef?.current;
          if (!aktif || !KOK_TABANLI_SAYFALAR.has(aktif)) setActivePage("home");
        }
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [modalContent]); // eslint-disable-line react-hooks/exhaustive-deps
}

// ─── useDiscoveryPopstateSync — PR-V3e2b2: discovery overlay back/forward ─────
// Her popstate'te URL'den discovery state türetip `mlv3:discovery-sync` yayar →
// FeedContainerV3 listener'ı (V3e2b1) overlay'i aç/kapat. Discovery-dışı path →
// state:null (overlay kapat niyeti; listener zaten kapalıysa no-op). AYRI ve
// BAĞIMSIZ window listener: usePopstateHandler (sayfa/modal çözümleme) ve
// FeedContainerV3.onPopClearScope (profil-scope) mantığına DOKUNMAZ, onlarla
// çakışmaz. Overlay AÇILIŞI pushState ürettiğinden (pushDiscoveryUrl) GERİ tuşu
// pushed entry'yi pop eder → bu handler senkronlar. İleri tuşu da aynı yolla açar.
export function useDiscoveryPopstateSync() {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onPop = () => {
      try {
        const detail = buildDiscoverySyncDetail(
          window.location.pathname,
          window.location.search || "",
          "popstate",
        );
        window.dispatchEvent(new CustomEvent(DISCOVERY_SYNC_EVENT, { detail }));
      } catch (_) {
        /* URL kozmetik senkron — hata feed'i etkilemez */
      }
      // PR-V3e4b — feed tab back/forward: aynı popstate'te tab-sync de yay →
      // FeedContainerV3 tab listener activeTab'i senkronlar. AYRI event; overlay
      // (tab:null) durumunda listener no-op (tab'ı değiştirmez).
      try {
        // Sorgu da geçilir: `/c/{id}?tab=...` geri/ileri'de sekmeyi geri
        // yükler. Sekmesiz URL'lerde detail birebir eskisi gibi tab:null.
        const tabDetail = buildTabSyncDetail(
          window.location.pathname,
          window.location.search || "",
        );
        window.dispatchEvent(new CustomEvent(TAB_SYNC_EVENT, { detail: tabDetail }));
      } catch (_) {
        /* tab senkron kozmetik — hata feed'i etkilemez */
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
}
